// src/components/MenuSelector.js
import React, {useEffect, useMemo, useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {menuApi} from '../api/menu';

const TYPE_LABELS = {
    appetizer: 'Закуска',
    main_dish: 'Основное блюдо',
    drink: 'Напиток',
};

const TYPE_ORDER = ['appetizer', 'main_dish', 'drink'];

const MenuSelector = ({token, member, family}) => {
    const [isOpen, setIsOpen] = useState(false);       // опрос активен?
    const [items, setItems] = useState([]);
    const [selections, setSelections] = useState([]);  // уже сделанные выборы (для текущего гостя и семьи)
    const [canVoteForOthers, setCanVoteForOthers] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    const loadStatus = async () => {
        try {
            const res = await menuApi.getStatus(token);
            setIsOpen(res.data.is_open);
            setItems(res.data.items || []);
            setSelections(res.data.selections || []);
            setCanVoteForOthers(!!res.data.can_vote_for_others);
        } catch (e) {
            setIsOpen(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Члены, за которых должен ответить текущий гость
    const familyMembers = family?.members ?? [];
    const targetMembers = canVoteForOthers
        ? familyMembers
        : familyMembers.filter((l) => l.member.id === member.id);

    const selectionsByMember = useMemo(() => {
        const map = {};
        selections.forEach((s) => { map[s.member_id] = s; });
        return map;
    }, [selections]);

    // Выбор считается завершённым, когда все члены группы ответили
    const done = targetMembers.length > 0 && targetMembers.every((l) => selectionsByMember[l.member.id]);

    if (loading) return null;
    if (!isOpen || done) return null;

    return (
        <>
            <AnimatePresence>
                {showModal && (
                    <MenuModal
                        token={token}
                        member={member}
                        items={items}
                        targetMembers={targetMembers}
                        selectionsByMember={selectionsByMember}
                        canVoteForOthers={canVoteForOthers}
                        onClose={() => setShowModal(false)}
                        onSaved={() => {
                            setShowModal(false);
                            loadStatus();
                        }}
                    />
                )}
            </AnimatePresence>

            <div className="w-full flex justify-center py-8 px-4">
                <motion.button
                    onClick={() => setShowModal(true)}
                    className="px-10 py-4 bg-gold-accent text-white text-lg uppercase tracking-[0.3em] rounded-full font-bold shadow-lg hover:bg-yellow-600 hover:scale-105 transition-all duration-300"
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.95}}
                >
                    Выбрать меню
                </motion.button>
            </div>
        </>
    );
};

const MenuModal = ({token, member, items, targetMembers, selectionsByMember, canVoteForOthers, onClose, onSaved}) => {
    // state: choices[member_id] = {appetizer, main_dish, drink}
    const [choices, setChoices] = useState(() => {
        const init = {};
        targetMembers.forEach((link) => {
            const existing = selectionsByMember[link.member.id];
            init[link.member.id] = {
                appetizer: existing?.appetizer_id ?? '',
                main_dish: existing?.main_dish_id ?? '',
                drink: existing?.drink_id ?? '',
            };
        });
        return init;
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const itemsByType = {};
    TYPE_ORDER.forEach((t) => { itemsByType[t] = items.filter((i) => i.item_type === t); });

    const setChoice = (memberId, type, value) => {
        setChoices((prev) => ({...prev, [memberId]: {...prev[memberId], [type]: value}}));
        setError('');
    };

    const allFilled = targetMembers.every((link) => {
        const c = choices[link.member.id];
        return c && c.appetizer && c.main_dish && c.drink;
    });

    const submit = async () => {
        if (!allFilled) {
            setError('Пожалуйста, выберите закуску, основное блюдо и напиток для каждого гостя');
            return;
        }
        setSaving(true);
        setError('');
        const payload = targetMembers.map((link) => ({
            member_id: link.member.id,
            appetizer_id: choices[link.member.id].appetizer,
            main_dish_id: choices[link.member.id].main_dish,
            drink_id: choices[link.member.id].drink,
        }));
        try {
            await menuApi.saveChoices(token, payload);
            onSaved();
        } catch (e) {
            setError(e.response?.data?.detail || 'Не удалось сохранить выбор');
            setSaving(false);
        }
    };

    return (
        <motion.div
            className="fixed inset-0 flex items-center justify-center bg-black/60 z-50 p-4 overflow-y-auto"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            onClick={onClose}
        >
            <motion.div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-auto"
                initial={{scale: 0.9, opacity: 0, y: 20}}
                animate={{scale: 1, opacity: 1, y: 0}}
                exit={{scale: 0.9, opacity: 0, y: 20}}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-gray-100">
                    <h3 className="text-2xl font-display text-center">Выбор меню</h3>
                    <p className="text-center text-gray-500 text-sm mt-1">
                        {canVoteForOthers
                            ? 'Вы отвечаете за всех членов вашей семьи'
                            : 'Выберите ваши предпочтения'}
                    </p>
                </div>

                <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
                    {targetMembers.map((link) => {
                        const c = choices[link.member.id] || {appetizer: '', main_dish: '', drink: ''};
                        return (
                            <div key={link.member.id} className="bg-gray-50 rounded-xl p-4">
                                <h4 className="font-semibold mb-3">
                                    {link.member.id === member?.id ? 'Вы' : `${link.member.last_name} ${link.member.first_name}`}
                                </h4>
                                <div className="space-y-3">
                                    {TYPE_ORDER.map((type) => (
                                        <div key={type}>
                                            <label className="block text-sm text-gray-600 mb-1">{TYPE_LABELS[type]}</label>
                                            <select
                                                className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-gold-accent focus:border-transparent"
                                                value={c[type]}
                                                onChange={(e) => setChoice(link.member.id, type, e.target.value === '' ? '' : Number(e.target.value))}
                                            >
                                                <option value="">— Выберите {TYPE_LABELS[type].toLowerCase()} —</option>
                                                {itemsByType[type].map((item) => (
                                                    <option key={item.id} value={item.id}>{item.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="p-6">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-amber-800 text-center">
                            ⚠️ Выбор можно сделать только один раз. После подтверждения изменить его будет нельзя.
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-100 text-red-700 rounded-lg p-3 mb-4 text-sm text-center">
                            {error}
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 transition font-medium"
                        >
                            Отмена
                        </button>
                        <button
                            onClick={submit}
                            disabled={saving || !allFilled}
                            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition ${saving || !allFilled ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gold-accent text-white hover:bg-yellow-600'}`}
                        >
                            {saving ? 'Сохранение...' : 'Подтвердить выбор'}
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default MenuSelector;